import { KeyboardEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { classNames } from 'primereact/utils';
import {
  CommentIcon,
  DownChevronIcon,
  FlagIcon,
  InfoOutlineIcon,
  LogoutIcon,
  SettingsOutlineIcon,
  UserAvatar,
} from '@repo/atomic-components';
import { selectUserAboutData, useAppSelector } from '@/store';
import { AppRoutes } from '@/constants';
import { useFocusTrap } from '@/hooks';
import { UserRoleLabels } from '@/api';
import { useSettingsHub } from './SettingsHub';
import { useSupportComposeHrefs } from './useSupportComposeHrefs';

type UserProfileMenuProps = {
  onLogout: () => void;
  /** Shows the user's name (and organisation) next to the avatar. Used in the expanded sidebar. */
  showIdentity?: boolean;
  organisationName?: string | null;
  /** Called when the menu opens or closes, so the sidebar can stay expanded meanwhile. */
  onOpenChange?: (open: boolean) => void;
};

/** Account menu: identity, settings hub, report an issue / suggest an improvement, and log out. */
export const UserProfileMenu = ({
  onLogout,
  showIdentity = false,
  organisationName,
  onOpenChange,
}: UserProfileMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  // Fixed position for the portalled menu, next to the trigger and outside the sidebar's clip.
  const [menuPos, setMenuPos] = useState<{ left: number; bottom: number } | null>(null);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const userAboutData = useAppSelector(selectUserAboutData);
  const { reportIssueHref, suggestImprovementHref } = useSupportComposeHrefs();
  const { openSettings } = useSettingsHub();

  // The menu is portalled to the end of <body>, so without the trap Tab from the trigger skips it.
  const setFocusTrapRef = useFocusTrap<HTMLDivElement>({ active: isOpen });
  const setMenuRef = useCallback(
    (node: HTMLDivElement | null) => {
      menuRef.current = node;
      setFocusTrapRef(node);
    },
    [setFocusTrapRef],
  );

  const handleMenuKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setIsOpen(false);
    }
  };

  const handleOpenSettings = () => {
    // The hub returns focus to whatever had it on open, and the menu item is about to unmount.
    buttonRef.current?.focus();
    setIsOpen(false);
    openSettings();
  };

  const handleAvatarClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (!isOpen) {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) {
        setMenuPos({
          left: Math.round(rect.right + 8),
          bottom: Math.round(window.innerHeight - rect.bottom),
        });
      }
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | globalThis.MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside as EventListener);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside as EventListener);
    };
  }, [isOpen]);

  return (
    <div className="relative flex h-full w-full items-center justify-items-end">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleAvatarClick}
        aria-label={userAboutData?.name ? `Open account menu for ${userAboutData.name}` : 'User profile'}
        aria-haspopup="true"
        aria-expanded={isOpen}
        className={classNames(
          'group hover:!bg-theme-white/90 hover:border-theme-border-grey/50 focus-visible:outline-fletchers-green-500 flex h-full w-full cursor-pointer items-center rounded-md border border-transparent p-2 transition-shadow hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
          showIdentity ? 'gap-2' : 'justify-center',
        )}
      >
        {/* `/user/about` has a single `name`, so `UserAvatar` splits it itself. */}
        <UserAvatar name={userAboutData?.name} fontSize={14} aria-label={userAboutData?.name ?? ''} />
        {showIdentity && (
          <>
            <span className="flex min-w-0 flex-1 flex-col text-left">
              <span className="text-theme-black truncate text-sm font-semibold">{userAboutData?.name}</span>
              {organisationName && <span className="text-theme-text-grey truncate text-xs">{organisationName}</span>}
            </span>
            <span
              aria-hidden
              className="text-theme-text-grey shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
            >
              <DownChevronIcon className="-rotate-90" width={16} height={16} />
            </span>
          </>
        )}
      </button>
      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={setMenuRef}
            onKeyDown={handleMenuKeyDown}
            style={{ position: 'fixed', left: menuPos.left, bottom: menuPos.bottom, maxHeight: 'calc(100vh - 16px)' }}
            className="bg-theme-light-background-grey text-theme-text-grey border-theme-border-grey z-[100] max-w-[calc(100vw-16px)] overflow-y-auto rounded-md border shadow-md"
          >
            <div className="px-6 pt-6 pb-4">
              <div className="text-theme-black text-[1rem] font-semibold">{userAboutData?.name}</div>
              <div className="text-theme-black mt-0.5 text-sm">
                {UserRoleLabels[userAboutData?.role as keyof typeof UserRoleLabels]}
              </div>
            </div>

            <div className="px-6 py-3">
              <button
                type="button"
                onClick={handleOpenSettings}
                aria-haspopup="dialog"
                className="text-theme-black flex cursor-pointer items-center gap-2 text-left text-sm font-semibold"
              >
                <SettingsOutlineIcon className="shrink-0" width={16} height={16} aria-hidden />
                <span>Settings</span>
              </button>
            </div>

            <div className="px-6 py-3">
              <a
                href={reportIssueHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="text-theme-black flex cursor-pointer items-center gap-2 text-left text-sm font-semibold"
              >
                <FlagIcon width="15" height="15" aria-hidden />
                <span>Report an issue</span>
              </a>
            </div>

            <div className="px-6 py-3">
              <a
                href={suggestImprovementHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="text-theme-black flex cursor-pointer items-center gap-2 text-left text-sm font-semibold"
              >
                <CommentIcon width="16" height="16" aria-hidden />
                <span>Suggest an improvement</span>
              </a>
            </div>

            <div className="px-6 py-3">
              <Link
                to={`/${AppRoutes.HowWeFletch}`}
                onClick={() => setIsOpen(false)}
                className="text-theme-black flex cursor-pointer items-center gap-2 text-left text-sm font-semibold"
              >
                <InfoOutlineIcon width="16" height="16" aria-hidden />
                <span>How we're using Fletch</span>
              </Link>
            </div>

            <div className="px-6 py-4">
              <button
                type="button"
                onClick={onLogout}
                className="text-theme-black flex w-fit cursor-pointer items-center gap-2 text-left text-sm font-semibold"
              >
                <LogoutIcon className="text-theme-black" />
                <span>Log out</span>
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
